import { useState, useEffect } from 'react';
import { Loader2, Sparkles } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { articleTranslationService } from '@/lib/services/articleTranslationService';
import { toast } from 'sonner';

interface Article {
  id: string;
  title: { ar: string; en: string; } | string; // Support both old and new format
  excerpt: { ar: string; en: string; } | string;
  content: { ar: string; en: string; } | string;
  author: { ar: string; en: string; } | string; // Support both old and new format
  category: { ar: string; en: string; } | string; // Support both old and new format
  image_url: string;
  read_time: number;
  created_at: string;
}

interface EditArticleModalProps {
  article: Article;
  isOpen: boolean;
  onClose: () => void;
  onSave: (article: Article) => Promise<void>;
}

export function EditArticleModal({ article, isOpen, onClose, onSave }: EditArticleModalProps) {
  const [editedArticle, setEditedArticle] = useState<Article>(article);
  const [isSaving, setIsSaving] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);

  // Convert old format to new format
  const normalizeArticle = (art: Article): Article => {
    return {
      ...art,
      title: typeof art.title === 'string' ? { ar: art.title, en: '' } : art.title,
      excerpt: typeof art.excerpt === 'string' ? { ar: art.excerpt, en: '' } : art.excerpt,
      content: typeof art.content === 'string' ? { ar: art.content, en: '' } : art.content,
      author: typeof art.author === 'string' ? { ar: art.author, en: '' } : art.author,
      category: typeof art.category === 'string' ? { ar: art.category, en: '' } : art.category,
    };
  };

  useEffect(() => {
    setEditedArticle(normalizeArticle(article));
  }, [article]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      await onSave(editedArticle);
      onClose();
    } catch (error) {
      console.error('Error saving article:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleTranslateToEnglish = async () => {
    const normalized = normalizeArticle(editedArticle);
    const titleAr = typeof normalized.title === 'string' ? normalized.title : normalized.title.ar;
    const excerptAr = typeof normalized.excerpt === 'string' ? normalized.excerpt : normalized.excerpt.ar;
    const contentAr = typeof normalized.content === 'string' ? normalized.content : normalized.content.ar;

    if (!titleAr || !excerptAr || !contentAr) {
      toast.error('الرجاء ملء المحتوى العربي أولاً');
      return;
    }

    setIsTranslating(true);
    toast.info('🤖 جاري الترجمة إلى الإنجليزية...');

    try {
      const result = await articleTranslationService.translateToEnglish(
        titleAr,
        excerptAr,
        contentAr
      );

      setEditedArticle({
        ...editedArticle,
        title: { ar: titleAr, en: result.titleEn },
        excerpt: { ar: excerptAr, en: result.excerptEn },
        content: { ar: contentAr, en: result.contentEn },
      });

      toast.success('✅ تمت الترجمة بنجاح!');
    } catch (error) {
      console.error('Translation error:', error);
      toast.error('❌ فشلت الترجمة. خدمة الذكاء الاصطناعي غير متاحة — اطلب من المسؤول التحقق من مفتاح الخادم.');
    } finally {
      setIsTranslating(false);
    }
  };

  const handleTranslateToArabic = async () => {
    const normalized = normalizeArticle(editedArticle);
    const titleEn = typeof normalized.title === 'string' ? '' : normalized.title.en;
    const excerptEn = typeof normalized.excerpt === 'string' ? '' : normalized.excerpt.en;
    const contentEn = typeof normalized.content === 'string' ? '' : normalized.content.en;

    if (!titleEn || !excerptEn || !contentEn) {
      toast.error('Please fill in the English content first');
      return;
    }

    setIsTranslating(true);
    toast.info('🤖 Translating to Arabic...');

    try {
      const result = await articleTranslationService.translateToArabic(
        titleEn,
        excerptEn,
        contentEn
      );

      setEditedArticle({
        ...editedArticle,
        title: { ar: result.titleAr, en: titleEn },
        excerpt: { ar: result.excerptAr, en: excerptEn },
        content: { ar: result.contentAr, en: contentEn },
      });

      toast.success('✅ Translation completed successfully!');
    } catch (error) {
      console.error('Translation error:', error);
      toast.error('❌ Translation failed. The AI service is unavailable — ask an administrator to check the server key.');
    } finally {
      setIsTranslating(false);
    }
  };

  const normalized = normalizeArticle(editedArticle);
  const titleAr = typeof normalized.title === 'string' ? normalized.title : normalized.title.ar;
  const titleEn = typeof normalized.title === 'string' ? '' : normalized.title.en;
  const excerptAr = typeof normalized.excerpt === 'string' ? normalized.excerpt : normalized.excerpt.ar;
  const excerptEn = typeof normalized.excerpt === 'string' ? '' : normalized.excerpt.en;
  const contentAr = typeof normalized.content === 'string' ? normalized.content : normalized.content.ar;
  const contentEn = typeof normalized.content === 'string' ? '' : normalized.content.en;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-7xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>تعديل المقال</span>
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={handleTranslateToEnglish}
                disabled={isTranslating || !titleAr}
                className="gap-2"
              >
                {isTranslating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
                ترجمة للإنجليزية
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={handleTranslateToArabic}
                disabled={isTranslating || !titleEn}
                className="gap-2"
              >
                {isTranslating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
                Translate to Arabic
              </Button>
            </div>
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Title - Side by Side */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">العنوان / Title</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="title-ar" className="text-sm text-muted-foreground">عربي</Label>
                <Input
                  id="title-ar"
                  type="text"
                  value={titleAr}
                  onChange={(e) => setEditedArticle({
                    ...editedArticle,
                    title: { ...normalized.title as any, ar: e.target.value }
                  })}
                  required
                  dir="rtl"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="title-en" className="text-sm text-muted-foreground">English</Label>
                <Input
                  id="title-en"
                  type="text"
                  value={titleEn}
                  onChange={(e) => setEditedArticle({
                    ...editedArticle,
                    title: { ...normalized.title as any, en: e.target.value }
                  })}
                  placeholder="English title"
                  dir="ltr"
                />
              </div>
            </div>
          </div>

          {/* Excerpt - Side by Side */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">المقدمة / Excerpt</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="excerpt-ar" className="text-sm text-muted-foreground">عربي</Label>
                <Textarea
                  id="excerpt-ar"
                  value={excerptAr}
                  onChange={(e) => setEditedArticle({
                    ...editedArticle,
                    excerpt: { ...normalized.excerpt as any, ar: e.target.value }
                  })}
                  rows={3}
                  required
                  dir="rtl"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="excerpt-en" className="text-sm text-muted-foreground">English</Label>
                <Textarea
                  id="excerpt-en"
                  value={excerptEn}
                  onChange={(e) => setEditedArticle({
                    ...editedArticle,
                    excerpt: { ...normalized.excerpt as any, en: e.target.value }
                  })}
                  rows={3}
                  placeholder="English excerpt"
                  dir="ltr"
                />
              </div>
            </div>
          </div>

          {/* Content - Side by Side */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">المحتوى / Content</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="content-ar" className="text-sm text-muted-foreground">عربي</Label>
                <Textarea
                  id="content-ar"
                  value={contentAr}
                  onChange={(e) => setEditedArticle({
                    ...editedArticle,
                    content: { ...normalized.content as any, ar: e.target.value }
                  })}
                  rows={8}
                  required
                  dir="rtl"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="content-en" className="text-sm text-muted-foreground">English</Label>
                <Textarea
                  id="content-en"
                  value={contentEn}
                  onChange={(e) => setEditedArticle({
                    ...editedArticle,
                    content: { ...normalized.content as any, en: e.target.value }
                  })}
                  rows={8}
                  placeholder="English content"
                  dir="ltr"
                />
              </div>
            </div>
          </div>

          {/* Author and Category fields - Bilingual */}
          <div className="space-y-4">
            {/* Author */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">الكاتب / Author</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="author-ar" className="text-sm text-muted-foreground">عربي</Label>
                  <Input
                    id="author-ar"
                    value={typeof normalized.author === 'string' ? normalized.author : normalized.author.ar}
                    onChange={(e) => setEditedArticle({
                      ...editedArticle,
                      author: { ...normalized.author as any, ar: e.target.value }
                    })}
                    placeholder="مثال: أحمد محمد"
                    required
                    dir="rtl"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="author-en" className="text-sm text-muted-foreground">English</Label>
                  <Input
                    id="author-en"
                    value={typeof normalized.author === 'string' ? '' : normalized.author.en}
                    onChange={(e) => setEditedArticle({
                      ...editedArticle,
                      author: { ...normalized.author as any, en: e.target.value }
                    })}
                    placeholder="Example: Ahmad Mohammad"
                    dir="ltr"
                  />
                </div>
              </div>
            </div>

            {/* Category */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">التصنيف / Category</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="category-ar" className="text-sm text-muted-foreground">عربي</Label>
                  <Input
                    id="category-ar"
                    value={typeof normalized.category === 'string' ? normalized.category : normalized.category.ar}
                    onChange={(e) => setEditedArticle({
                      ...editedArticle,
                      category: { ...normalized.category as any, ar: e.target.value }
                    })}
                    placeholder="مثال: صحة"
                    required
                    dir="rtl"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="category-en" className="text-sm text-muted-foreground">English</Label>
                  <Input
                    id="category-en"
                    value={typeof normalized.category === 'string' ? '' : normalized.category.en}
                    onChange={(e) => setEditedArticle({
                      ...editedArticle,
                      category: { ...normalized.category as any, en: e.target.value }
                    })}
                    placeholder="Example: Health"
                    dir="ltr"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Other fields */}
          <div className="grid grid-cols-2 gap-4" dir="rtl">
            <div>
              <Label htmlFor="image_url">رابط الصورة</Label>
              <Input
                id="image_url"
                type="url"
                value={editedArticle.image_url}
                onChange={(e) => setEditedArticle({ ...editedArticle, image_url: e.target.value })}
                required
                dir="ltr"
              />
            </div>
            <div>
              <Label htmlFor="read_time">وقت القراءة (بالدقائق)</Label>
              <Input
                id="read_time"
                type="number"
                value={editedArticle.read_time}
                onChange={(e) => setEditedArticle({ ...editedArticle, read_time: parseInt(e.target.value) })}
                min="1"
                required
              />
            </div>
          </div>
          <div className="flex justify-end gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSaving || isTranslating}
            >
              إلغاء
            </Button>
            <Button
              type="submit"
              disabled={isSaving || isTranslating}
            >
              {isSaving ? 'جاري الحفظ...' : 'حفظ التغييرات'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

